import { arrow, autoUpdate, computePosition, shift, size } from "@floating-ui/dom";

import { createElement } from "./dom";

/**
 * A convenience method for rendering popups using the Floating UI library.
 *
 * @typedef {object} PopupElements
 * @property {HTMLElement} [arrow] An optional arrow or caret element.
 * @property {HTMLElement} floating The popup element.
 * @property {HTMLElement} reference The anchor element.
 *
 * @typedef {object} PopupBundle
 * @property {(() => void)} cleanup Cleans up popup side effects and should be called after the popup is hidden.
 * @property {(() => void)} render Computes the popup's positioning and assigns required styles.
 *
 * @param {PopupElements} elements The popup elements.
 * @returns {PopupBundle} Methods for mounting and unmounting the popups.
 */
export function getPopup(elements) {
  const setPosition = async () => {
    const offset = getContainerSize().gutter;

    // Compute position
    const position = await computePosition(elements.reference, elements.floating, {
      strategy: "fixed",
      placement: "bottom",
      middleware: [
        shift(() => ({ padding: { left: offset, right: offset } })),
        size({
          apply(args) {
            // Change styles, e.g.
            Object.assign(args.elements.floating.style, {
              maxWidth: `${args.availableWidth}px`,
              maxHeight: `${args.availableHeight}px`,
            });
          },
          padding: { left: offset, right: offset },
        }),
        elements.arrow && arrow({ element: elements.arrow }),
      ],
    });

    // Assign styles
    Object.assign(elements.floating.style, {
      left: `${position.x}px`,
      top: `${position.y}px`,
      position: position.strategy,
    });
    if (elements.arrow instanceof HTMLElement && position.middlewareData.arrow) {
      Object.assign(elements.arrow.style, {
        left: `${position.middlewareData.arrow.x || 0}px`,
        top: position.placement.includes("top") ? "" : 0,
        bottom: position.placement.includes("bottom") ? "" : 0,
      });
    }
  };

  let cleanup = () => {};

  const render = () => {
    cleanup = autoUpdate(elements.reference, elements.floating, setPosition);
  };

  return { cleanup, render };
}

/**
 * Returns sizing derived from the global .container class.
 *
 * @typedef {object} ContainerSize
 * @property {number} width The container width.
 * @property {number} gutter The container gutter.
 *
 * @returns {ContainerSize} Container width and gutter sizes.
 */
export function getContainerSize() {
  let container = document.getElementById("ifrs-container-ref");

  if (!container) {
    container = createElement("div", {
      "aria-hidden": "true",
      classList: "container",
      id: "ifrs-container-ref",
    });
    document.body.append(container);
  }

  return {
    width: container.clientWidth,
    gutter: (document.documentElement.clientWidth - container.clientWidth) / 2,
  };
}
